import type { Vec3 } from '@archon3d/core';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { ProjectileFlavour } from '../../game/projectileFlavour';

/** How a shot is drawn: its colour, its size against the plain ball, and how long it is drawn out. */
export interface ShotLook {
  color: readonly [number, number, number];
  size: number;
  /** Length over width along its flight: an arrow is a dart, a rock a ball. */
  stretch: number;
}

/**
 * Each flavour of shot its own look (client-arena:quality#9, x-aaa-gap#7): the Archer's arrow, the
 * Dragon's fire, a caster's bolt, the Troll's rock and the Basilisk's spit were the same orange
 * ball. The flavour comes from the character's stats, as its sound does, so content stays data.
 */
export const SHOT_LOOKS: Record<ProjectileFlavour, ShotLook> = {
  arrow: { color: [0.93, 0.87, 0.7], size: 0.42, stretch: 3.4 },
  fire: { color: [1, 0.48, 0.1], size: 1.3, stretch: 1.6 },
  bolt: { color: [0.62, 0.55, 1], size: 0.85, stretch: 2.4 },
  rock: { color: [0.52, 0.47, 0.41], size: 1.45, stretch: 1 },
  spit: { color: [0.52, 0.95, 0.28], size: 0.75, stretch: 1.4 },
};

const scale = new Vector3();
const turn = new Quaternion();
const at = new Vector3();

/** A shot's thin-instance matrix, drawn out along where it flies. */
export function shotMatrix(pos: Vec3, vel: Vec3 | undefined, look: ShotLook, out: Matrix): Matrix {
  const vx = vel?.x ?? 0;
  const vy = vel?.y ?? 0;
  const vz = vel?.z ?? 0;
  const flat = Math.hypot(vx, vz);
  // Local +z turned onto the velocity: yaw about y, then pitched up or down to its slope.
  Quaternion.RotationYawPitchRollToRef(Math.atan2(vx, vz), -Math.atan2(vy, flat), 0, turn);
  const moving = flat > 1e-6 || Math.abs(vy) > 1e-6;
  scale.set(look.size, look.size, look.size * (moving ? look.stretch : 1));
  at.set(pos.x, pos.y, pos.z);
  return Matrix.ComposeToRef(scale, turn, at, out);
}
